/* eslint-disable no-shadow */
import React, { useContext, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import { Logger } from '../../../../../ui-kitchen/src/utils';

// utils and constants
import { IDLE, PENDING } from '../../../../../ui-kitchen/src/constants/asyncLoadingStatuses';
import {
  getLegalText,
  getSpecs,
  handleErrorOnLoad,
  parsePaymentMethodId,
} from './reservationDetailsUtils';
import { getSearchParamsAsObject } from '../../../../../ui-kitchen/src/utils/locationUtils';
import { getURLSearchParams } from '../../../../../ui-kitchen/src/utils/searchParamUtils';
import { getTranslation } from '../../../../../ui-kitchen/src/providers/TranslationProvider';

// actions, thunks, selectors, reducers
import {
  attemptBook,
  commitDetailsThunk,
  getDetailsThunk,
  postBookingBookThunk,
  reset,
  resetBookingError,
  resetPayment,
  setAffId,
  setBookingFlowState,
  setBookOnBehalfOf,
  setBookOnBehalfOfPayload,
  setDay,
  setOfferId,
  setPartySize,
  setPaymentId,
  setReplace,
  setReservation,
  setServiceCharge,
  setSourceId,
  setUtmSource,
  setPartnerId,
  setVenueId,
  setVenueName,
  toggleVenueCommunicationOptIn,
} from '../../../../../ui-kitchen/src/reducers/bookingSlice';
import { getUserThunk, selectIsConcierge } from '../../../../../ui-kitchen/src/reducers/userSlice';
import { selectIsAuthenticated } from '../../../../../ui-kitchen/src/reducers/authTokenSlice';
import { setMarketingPageName } from '../../../../../ui-kitchen/src/reducers/amexEventsSlice';
import {
  setMetadataPageTitle, setMobileFirstLogin, setRouteBackPath, setRouteSearch,
} from '../../../../../ui-kitchen/src/reducers/resyRouterSlice';

// hooks and contexts
import PathBuilderContext from '../WidgetApp/PathBuilderContext';
import IframeHostContext from '../../contexts/iframeHost';
import {
  useAuthenticatedEffect, useHandleBookingError, useClearMessages, useFetchVenue, useTransformPaymentMethods, useSetServiceCharge,
} from './reservationDetailsHooks';

// components
import SelectCreditCardPage from '../SelectCreditCardPage';
import SummaryPage from '../SummaryPage/SummaryPage';
import { setMarketingCookie } from '../../../../../ui-kitchen/src/utils/resyUCMCookieUtils';

const ReservationDetailsController = (props) => {
  const {
    Analytics,
    attemptBook,
    bookingState,
    bookOnBehalfOf,
    canBook,
    canBookOnBehalf,
    commitDetailsThunk,
    day,
    errorBook,
    getDetailsThunk,
    getUserThunk,
    isAuthenticated,
    isCreditCardConfirmed,
    isIdle,
    isPending,
    partySize,
    paymentId,
    paymentMethods,
    postBookingBookThunk,
    preferredLanguage,
    reservationDetails,
    reservationFromState,
    reset,
    resetBookingError,
    resetPayment,
    serviceCharge,
    serviceChargeOptions,
    serviceChargeOptionsConfig,
    setAffId,
    setBookingFlowState,
    setBookOnBehalfOf,
    setBookOnBehalfOfPayload,
    setDay,
    setMetadataPageTitle,
    setMobileFirstLogin,
    setOfferId,
    setPartySize,
    setPaymentId,
    setReplace,
    setReservation,
    setRouteBackPath,
    setRouteSearch,
    setServiceCharge,
    setSourceId,
    setUtmSource,
    setPartnerId,
    setVenueId,
    setVenueName,
    toggleVenueCommunicationOptIn,
    triggerCycle,
    venueCommunicationOptIn,
    setMarketingPageName,
  } = props;

  // search params
  const searchParams = getURLSearchParams();
  const dateFromSearch = searchParams.get('date');
  const partySizeFromSearch = searchParams.get('seats');
  const tableTypeFromSearch = searchParams.get('tableType');
  const returnUrlFromSearch = searchParams.get('returnUrl');
  const reservationSerialized = searchParams.get('reservation');
  let reservationFromSearch = null;
  try {
    reservationFromSearch = reservationSerialized && JSON.parse(reservationSerialized);
  } catch (error) {
    Logger.error('[ReservationDetailsController] Error parsing reservation from search params:', error);
  }
  const venueIdFromSearch = searchParams.get('venueId');
  const marketingPage = searchParams.get('marketingPage');
  if (marketingPage) setMarketingPageName(marketingPage);
  const venueId = bookingState?.venueId || venueIdFromSearch;

  // pathbuilder
  const pathBuilder = useContext(PathBuilderContext);
  pathBuilder.setForceSummary(true);
  const backPath = pathBuilder.backPath('Summary');
  if (venueId && reservationFromState) {
    pathBuilder.setValues({
      venueId,
      addOnsTemplateId: reservationFromState?.hasMenus && reservationFromState?.templateId,
    });
  }

  // iframehost
  const iframeHost = useContext(IframeHostContext);
  const viewPortHeight = iframeHost.getDimensions().innerHeight;

  // resyRouter dispatches
  setRouteBackPath(backPath);
  setRouteSearch({
    date: day,
    seats: partySize,
    ...(returnUrlFromSearch ? { returnUrl: returnUrlFromSearch } : {}),
  });

  // START Hooks
  const navigate = useNavigate();

  // Primary state
  const [detailsState, setDetailsState] = useState({
    messages: [],
    initServiceCharge: false,
    paymentMethodsList: [],
    selectedPaymentMethodId: undefined,
    venueState: {},
  });

  // messages
  const setMessages = (messages) => setDetailsState((prevState) => ({ ...prevState, messages }));
  const clearMessages = () => setMessages([]);
  const isIdleOrPending = isIdle || isPending;
  useClearMessages({
    isIdleOrPending,
    messages: detailsState.messages,
    clearMessages,
  });

  // one-off 303 status in searchParams handler from legacy controller
  handleErrorOnLoad(getSearchParamsAsObject(), setMessages);

  // errorBook
  useHandleBookingError({
    backPath: pathBuilder.backPath(),
    clearMessages,
    errorBook,
    resetBookingError,
    setMessages,
  });

  // serviceCharge
  useSetServiceCharge({
    initServiceCharge: detailsState.initServiceCharge,
    setDetailsState,
    setServiceCharge,
    serviceCharge,
    serviceChargeOptionsConfig,
  });

  // Payment Methods
  const cardLabel = getTranslation({
    translationKey: 'credit_card_label',
    fallback: 'Credit card ending in',
  });
  useAuthenticatedEffect({
    isAuthenticated, getUserThunk, resetPayment, setBookingFlowState,
  });
  useTransformPaymentMethods({
    cardLabel,
    paymentMethods,
    selectedPaymentMethodId: detailsState.selectedPaymentMethodId,
    setDetailsState,
    setPaymentId,
  });
  const onPaymentMethodSelect = (item) => setDetailsState((prevState) => ({ ...prevState, selectedPaymentMethodId: parsePaymentMethodId(item) }));

  // Venue
  useFetchVenue({ setDetailsState, venueId });

  // Init
  useEffect(() => {
    // Bootstrap from URL params once. After this point, rely on booking state.
    if (!bookingState?.partySize && partySizeFromSearch) {
      setPartySize(Number(partySizeFromSearch));
    }
    if (!bookingState?.day && dateFromSearch) {
      setDay(dateFromSearch);
    }

    // if there is a URL param of venueId set the venueId
    if (venueIdFromSearch) {
      setVenueId(venueIdFromSearch);
    }

    // if there is a URL param of tableConfigId set the reservation
    if (reservationFromSearch?.token || reservationFromSearch?.roomId) {
      setReservation(reservationFromSearch);
    }

    // whether there is a URL param or not, if a user makes it to this screen,
    // then there are reservation details to be retrieved.
    // get the details from 3/details
    // get details gets all options for a reservation
    getDetailsThunk();

    // post details commits to a default option
    // which may then be updated eg changing gratuity
    commitDetailsThunk();
  }, []);

  // used to cleanup ucm cookie listener
  let marketingCookieCleanup = () => {};
  useEffect(() => {
    if (bookingState?.reservation?.roomId) {
      // set Reserve with Goggle cookies if the reservation has a roomId
      try {
        const { reservation } = bookingState;
        const { rwgToken, venueId } = reservation;
        marketingCookieCleanup = setMarketingCookie('_rwg', `${venueId}~${rwgToken}`, {
          expires: moment().add(30, 'days').toDate(),
        });
      } catch (e) {
        Logger.error('[ReservationDetailsController] setMarketingCookie Error:', e);
      }
    }
    return () => {
      if (marketingCookieCleanup) marketingCookieCleanup();
    };
  }, [bookingState]);
  // END Hooks

  // Stateful values
  const {
    messages,
    paymentMethodsList,
    selectedPaymentMethodId,
    venueState,
  } = detailsState;

  // Pre-render checks
  if (venueState?.featureRecaptcha && bookingState.errorDetail?.status === 403 && !bookingState.captchaResponse) {
    const captchaSearchParams = new URLSearchParams();
    const captchaVenueId = bookingState.venueId || venueIdFromSearch;
    const captchaSeats = bookingState.partySize
      || bookingState.reservation?.seats
      || partySizeFromSearch;
    const captchaDate = bookingState.day || dateFromSearch;

    if (captchaVenueId) {
      captchaSearchParams.set('venueId', captchaVenueId);
    }
    if (captchaSeats) {
      captchaSearchParams.set('seats', captchaSeats);
    }
    if (captchaDate) {
      captchaSearchParams.set('date', captchaDate);
    }
    if (bookingState.reservation) {
      captchaSearchParams.set('reservation', JSON.stringify(bookingState.reservation));
    }

    const captchaPath = captchaSearchParams.toString()
      ? `/captcha?${captchaSearchParams.toString()}`
      : '/captcha';

    setReservation(bookingState.reservation);
    navigate(captchaPath, triggerCycle);
  }

  if (canBookOnBehalf && bookOnBehalfOf === undefined) {
    setBookOnBehalfOf(true);
  }

  // Computed values
  let legalText = '';
  if (!isEmpty(venueState)) {
    setVenueName(venueState.name);
    legalText = getLegalText(venueState);
    setMobileFirstLogin(venueState.isMobileFirstLogin);
    const title = getTranslation({
      translationKey: 'titles.reservation_details_venue',
      translationValues: { venueName: venueState.name },
      fallback: `${venueState.name} Reservation Details`,
    });
    setMetadataPageTitle(title);
  }

  const hasReservationFromState = !isEmpty(reservationFromState);
  const reservationInfo = hasReservationFromState ? reservationFromState : reservationFromSearch;
  const partySizeFromSearchResolved = partySizeFromSearch ? Number(partySizeFromSearch) : undefined;
  const partySizeResolved = partySize
    || reservationInfo?.partySize
    || reservationInfo?.seats
    || partySizeFromSearchResolved;
  let dateTimeResovled = dateFromSearch;
  let tableTypeResolved = tableTypeFromSearch;
  if (reservationInfo) {
    dateTimeResovled = reservationInfo?.time || dateFromSearch;
    tableTypeResolved = reservationInfo?.type || tableTypeFromSearch;
  }
  const specs = getSpecs(dateTimeResovled, partySizeResolved, tableTypeResolved);

  const bookingActionsAndThunks = {
    attemptBook,
    commitDetailsThunk,
    getDetailsThunk,
    postBookingBookThunk,
    reset,
    resetBookingError,
    setAffId,
    setBookOnBehalfOf,
    setBookOnBehalfOfPayload,
    setDay,
    setOfferId,
    setPartySize,
    setPaymentId,
    setReplace,
    setReservation,
    setServiceCharge,
    setSourceId,
    setUtmSource,
    setPartnerId,
    setVenueId,
    setVenueName,
  };

  return isCreditCardConfirmed
    ? (
      <SelectCreditCardPage
        Analytics={Analytics}
        backPath={backPath}
        bookingState={bookingState}
        bookOnBehalfOf={bookOnBehalfOf}
        canBook={canBook}
        canBookOnBehalf={canBookOnBehalf}
        clearError={clearMessages}
        errors={messages}
        legalText={legalText}
        navigate={navigate}
        onPaymentMethodSelect={onPaymentMethodSelect}
        paymentId={paymentId}
        paymentMethods={paymentMethodsList}
        pending={isPending}
        preferredLanguage={preferredLanguage}
        reservationDetails={reservationDetails}
        reservationInfo={reservationInfo}
        selectedPaymentMethod={selectedPaymentMethodId}
        serviceChargeOptions={serviceChargeOptions}
        specs={specs}
        thunks={bookingActionsAndThunks}
        toggleVenueCommunicationOptIn={toggleVenueCommunicationOptIn}
        venueCommunicationOptIn={venueCommunicationOptIn}
        venueInfo={venueState}
        viewPortHeight={viewPortHeight}
      />
    ) : (
      <SummaryPage
        Analytics={Analytics}
        backPath={backPath}
        bookingState={bookingState}
        bookOnBehalfOf={bookOnBehalfOf}
        canBook={canBook}
        canBookOnBehalf={canBookOnBehalf}
        clearError={clearMessages}
        errors={messages}
        legalText={legalText}
        navigate={navigate}
        onPaymentMethodSelect={onPaymentMethodSelect}
        paymentMethods={paymentMethodsList}
        preferredLanguage={preferredLanguage}
        reservationInfo={reservationInfo}
        selectedPaymentMethod={selectedPaymentMethodId}
        serviceChargeOptions={serviceChargeOptions}
        specs={specs}
        thunks={bookingActionsAndThunks}
        toggleVenueCommunicationOptIn={toggleVenueCommunicationOptIn}
        venueCommunicationOptIn={venueCommunicationOptIn}
        venueInfo={venueState}
        viewPortHeight={viewPortHeight}
      />
    );
};

ReservationDetailsController.propTypes = {
  Analytics: PropTypes.shape({}).isRequired,
  bookingState: PropTypes.shape({
    captchaResponse: PropTypes.string,
    day: PropTypes.string,
    errorDetail: PropTypes.shape({
      status: PropTypes.number,
    }),
    partySize: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),
    reservation: PropTypes.shape({
      roomId: PropTypes.string,
      rwgToken: PropTypes.string,
      seats: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
      ]),
      venueId: PropTypes.number,
    }),
    venueId: PropTypes.number,
  }).isRequired,
  attemptBook: PropTypes.func.isRequired,
  bookOnBehalfOf: PropTypes.bool.isRequired,
  canBook: PropTypes.bool.isRequired,
  canBookOnBehalf: PropTypes.bool.isRequired,
  commitDetailsThunk: PropTypes.func.isRequired,
  day: PropTypes.string.isRequired,
  errorBook: PropTypes.shape({}),
  getDetailsThunk: PropTypes.func.isRequired,
  getUserThunk: PropTypes.func.isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  isCreditCardConfirmed: PropTypes.bool,
  isIdle: PropTypes.bool.isRequired,
  isPending: PropTypes.bool.isRequired,
  partySize: PropTypes.number,
  paymentId: PropTypes.string.isRequired,
  paymentMethods: PropTypes.arrayOf(PropTypes.shape({
    display: PropTypes.string,
    id: PropTypes.number,
    is_default: PropTypes.bool,
    type: PropTypes.string,
  })),
  postBookingBookThunk: PropTypes.func.isRequired,
  preferredLanguage: PropTypes.string,
  reservationDetails: PropTypes.shape({}),
  reservationFromState: PropTypes.shape({
    hasMenus: PropTypes.bool,
    templateId: PropTypes.number,
    time: PropTypes.string,
    type: PropTypes.string,
  }),
  reset: PropTypes.func.isRequired,
  resetBookingError: PropTypes.func.isRequired,
  resetPayment: PropTypes.func.isRequired,
  setAffId: PropTypes.func.isRequired,
  setBookingFlowState: PropTypes.func.isRequired,
  setBookOnBehalfOf: PropTypes.func.isRequired,
  setBookOnBehalfOfPayload: PropTypes.func.isRequired,
  setDay: PropTypes.func.isRequired,
  setMetadataPageTitle: PropTypes.func.isRequired,
  setMobileFirstLogin: PropTypes.func.isRequired,
  setOfferId: PropTypes.func.isRequired,
  setPartySize: PropTypes.func.isRequired,
  setPaymentId: PropTypes.func.isRequired,
  setReplace: PropTypes.func.isRequired,
  setReservation: PropTypes.func.isRequired,
  setRouteBackPath: PropTypes.func.isRequired,
  setRouteSearch: PropTypes.func.isRequired,
  setServiceCharge: PropTypes.func.isRequired,
  setSourceId: PropTypes.func.isRequired,
  setUtmSource: PropTypes.func.isRequired,
  setPartnerId: PropTypes.func.isRequired,
  setVenueId: PropTypes.func.isRequired,
  setVenueName: PropTypes.func.isRequired,
  serviceCharge: PropTypes.string,
  serviceChargeOptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  serviceChargeOptionsConfig: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  toggleVenueCommunicationOptIn: PropTypes.func.isRequired,
  triggerCycle: PropTypes.func.isRequired,
  venueCommunicationOptIn: PropTypes.bool.isRequired,
  setMarketingPageName: PropTypes.func.isRequired,
};

ReservationDetailsController.defaultProps = {
  errorBook: null,
  isCreditCardConfirmed: false,
  partySize: 2,
  paymentMethods: null,
  preferredLanguage: 'en-US',
  reservationDetails: {},
  reservationFromState: {},
  serviceCharge: undefined,
};

const mapStateToProps = (state) => {
  const isAuthenticated = selectIsAuthenticated(state);
  const canBookOnBehalf = selectIsConcierge(state);
  const { booking, user } = state;
  const {
    bookOnBehalfOf,
    canBook,
    day,
    detailsCommitted,
    errorBook,
    loadingBooking,
    loadingDetails,
    partySize,
    paymentId,
    reservation = {},
    serviceCharge,
    venueCommunicationOptIn,
  } = booking;
  const isIdle = loadingBooking === IDLE || loadingDetails === IDLE;
  const isPending = loadingBooking === PENDING || loadingDetails === PENDING;
  const serviceChargeOptions = booking.details?.payment?.options;
  const serviceChargeOptionsConfig = booking.details?.config?.service_charge_options;
  const { userData } = user;
  const { payment_methods: paymentMethods } = userData || {};
  return {
    bookingState: booking,
    bookOnBehalfOf,
    canBook,
    canBookOnBehalf,
    day,
    errorBook,
    isAuthenticated,
    isIdle,
    isPending,
    partySize,
    paymentId,
    paymentMethods,
    reservationDetails: detailsCommitted,
    reservationFromState: reservation,
    serviceCharge,
    serviceChargeOptions,
    serviceChargeOptionsConfig,
    venueCommunicationOptIn,
  };
};

const mapDispatchToProps = {
  attemptBook,
  postBookingBookThunk,
  getDetailsThunk,
  getUserThunk,
  commitDetailsThunk,
  reset,
  resetBookingError,
  resetPayment,
  setAffId,
  setBookingFlowState,
  setBookOnBehalfOf,
  setBookOnBehalfOfPayload,
  setDay,
  setMetadataPageTitle,
  setMobileFirstLogin,
  setOfferId,
  setPartySize,
  setPaymentId,
  setReplace,
  setReservation,
  setRouteBackPath,
  setRouteSearch,
  setServiceCharge,
  setSourceId,
  setUtmSource,
  setPartnerId,
  setVenueId,
  setVenueName,
  toggleVenueCommunicationOptIn,
  setMarketingPageName,
};

export { ReservationDetailsController as ComponentUnderTest, mapStateToProps };
export default connect(mapStateToProps, mapDispatchToProps)(ReservationDetailsController);
